import React, { useState, useMemo } from 'react';
import {
  Search,
  User,
  MapPin,
  Package,
  TrendingUp,
  ChevronRight,
  Mail,
  Phone,
  X,
} from 'lucide-react';

function UsersList({ users, onItemClick }) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;

    const search = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search) ||
        user.phone_number?.toLowerCase().includes(search)
    );
  }, [users, searchTerm]);

  const sortedUsers = useMemo(() => {
    return filteredUsers
      .map((user) => ({
        ...user,
        totalDeliveries: user.delivery_count || 0,
        totalOrders: user.order_count || 0,
        totalActivity: (user.delivery_count || 0) + (user.order_count || 0),
      }))
      .sort((a, b) => b.totalActivity - a.totalActivity);
  }, [filteredUsers]);

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'master':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'driver':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'user':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getRoleLabel = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'master':
        return 'Master';
      case 'driver':
        return 'Conductor';
      case 'user':
        return 'Cliente';
      default:
        return role || 'Usuario';
    }
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex-shrink-0 border-b border-slate-200 bg-white p-3">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#B1336E]/10">
            <User className="h-4 w-4 text-[#B1336E]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-800 font-montserrat">Usuarios</h2>
            <p className="truncate text-xs text-slate-500 font-montserrat">
              {sortedUsers.length} usuario{sortedUsers.length !== 1 ? 's' : ''} con actividad
            </p>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar usuario..."
            className="w-full rounded-md border border-slate-300 py-2 pl-10 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#B1336E] font-montserrat"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {sortedUsers.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
              <User className="h-8 w-8 text-slate-400" />
            </div>
            <p className="mb-1 text-sm font-medium text-slate-600 font-montserrat">
              {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios con actividad'}
            </p>
            <p className="text-xs text-slate-400 font-montserrat">
              {searchTerm ? 'Intenta con otra busqueda' : 'Selecciona otra fecha'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 p-2">
            {sortedUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => onItemClick(user.id)}
                className="group w-full rounded-md border border-slate-200 bg-white p-3 text-left transition-all hover:border-[#B1336E]/30 hover:bg-slate-50 hover:shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    {user.profile_image ? (
                      <img
                        src={user.profile_image}
                        alt={user.username}
                        className="h-10 w-10 rounded-full border-2 border-slate-200 object-cover transition-colors group-hover:border-[#B1336E]"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-200 bg-[#B1336E] transition-colors group-hover:border-[#B1336E]">
                        <span className="text-sm font-bold text-white font-montserrat">
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <h3 className="truncate font-semibold text-slate-800 font-montserrat">
                        {user.username || 'Usuario sin nombre'}
                      </h3>
                      <span
                        className={`flex-shrink-0 rounded border px-2 py-0.5 text-[10px] font-medium font-montserrat ${getRoleBadgeColor(
                          user.role
                        )}`}
                      >
                        {getRoleLabel(user.role)}
                      </span>
                    </div>

                    <div className="mb-2 space-y-1">
                      {user.email && (
                        <p className="flex items-center gap-1.5 truncate text-xs text-slate-500 font-montserrat">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </p>
                      )}
                      {user.phone_number && (
                        <p className="flex items-center gap-1.5 text-xs text-slate-500 font-montserrat">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          {user.phone_number}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      {user.totalDeliveries > 0 && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 text-[#B1336E]" />
                          <span className="text-xs font-medium text-slate-600 font-montserrat">
                            {user.totalDeliveries} entrega{user.totalDeliveries !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      {user.totalOrders > 0 && (
                        <div className="flex items-center gap-1">
                          <Package className="h-3.5 w-3.5 text-[#E6B43C]" />
                          <span className="text-xs font-medium text-slate-600 font-montserrat">
                            {user.totalOrders} recojo{user.totalOrders !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="mt-2 h-5 w-5 flex-shrink-0 text-slate-400 transition-colors group-hover:text-[#B1336E]" />
                </div>

                {user.provider_name && (
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="h-3 w-3 text-[#67A43C]" />
                      <span className="text-xs text-slate-600 font-montserrat">
                        Proveedor: <span className="font-medium">{user.provider_name}</span>
                      </span>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-slate-200 bg-slate-50 p-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-slate-200 bg-white p-2.5">
            <div className="mb-1 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#B1336E]" />
              <span className="text-xs font-medium text-slate-600 font-montserrat">Entregas</span>
            </div>
            <p className="text-xl font-bold text-slate-800 font-montserrat">
              {sortedUsers.reduce((sum, user) => sum + user.totalDeliveries, 0)}
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-2.5">
            <div className="mb-1 flex items-center gap-2">
              <Package className="h-4 w-4 text-[#E6B43C]" />
              <span className="text-xs font-medium text-slate-600 font-montserrat">Recojos</span>
            </div>
            <p className="text-xl font-bold text-slate-800 font-montserrat">
              {sortedUsers.reduce((sum, user) => sum + user.totalOrders, 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersList;
